import axios from 'axios'
import { apiBaseUrl, oidcClientId, oidcScope } from './config'

// Storage keys
const ACCESS = 'anchao_admin_access'
const REFRESH = 'anchao_admin_refresh'
const EXPIRES = 'anchao_admin_expires'

export function getAccessToken() {
  const exp = Number(localStorage.getItem(EXPIRES) || 0)
  if (exp && Date.now() > exp - 30_000) {
    // Treat as expired 30s before actual expiry to avoid races.
    return null
  }
  return localStorage.getItem(ACCESS) || null
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH) || null
}

export function clearTokens() {
  localStorage.removeItem(ACCESS)
  localStorage.removeItem(REFRESH)
  localStorage.removeItem(EXPIRES)
}

function persistTokenResponse(data) {
  localStorage.setItem(ACCESS, data.access_token)
  if (data.refresh_token) localStorage.setItem(REFRESH, data.refresh_token)
  if (data.expires_in) {
    localStorage.setItem(EXPIRES, String(Date.now() + data.expires_in * 1000))
  }
}

export async function login(username, password) {
  const body = new URLSearchParams()
  body.set('grant_type', 'password')
  body.set('username', username)
  body.set('password', password)
  body.set('scope', oidcScope)
  body.set('client_id', oidcClientId)

  const { data } = await axios.post(`${apiBaseUrl}/connect/token`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  persistTokenResponse(data)
  return data
}

// ---- 医生微信扫码登录 -------------------------------------------------------
// 会话接口是匿名端点，且调用时本来就没有 token：必须用裸 axios 直连，
// 不能走 api/request.js 那个实例（它注入 Bearer，并在 401/登录页 HTML 时
// 强制清账号态 + 跳登录页，会把登录页自己踢一遍）。

// POST /api/app/qr-login/session → { sessionId, qrCodeBase64, scene, expiresInSeconds }
// qrCodeBase64 是完整 data URI（"data:image/png;base64,…"）；开发环境生码失败时为 null。
export async function createQrSession() {
  const { data } = await axios.post(`${apiBaseUrl}/api/app/qr-login/session`)
  return data
}

// GET /api/app/qr-login/{id}/status → { status, expertName }
// status ∈ Pending | Scanned | Confirmed | Rejected | Expired（精确大小写）。
export async function getQrStatus(sessionId) {
  const { data } = await axios.get(
    `${apiBaseUrl}/api/app/qr-login/${encodeURIComponent(sessionId)}/status`,
  )
  return data
}

// 自定义 grant：会话 Confirmed 后拿 sessionId 换 token（后端签发即销毁会话，一次性）。
// 会话不存在/过期 → error=expired_session；尚未确认 → error=authorization_pending。
export async function loginByQr(sessionId) {
  const body = new URLSearchParams()
  body.set('grant_type', 'qr_login')
  body.set('session_id', sessionId)
  body.set('scope', oidcScope)
  body.set('client_id', oidcClientId)

  const { data } = await axios.post(`${apiBaseUrl}/connect/token`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  persistTokenResponse(data)
  return data
}

export async function refreshAccessToken() {
  const rt = getRefreshToken()
  if (!rt) throw new Error('no refresh token')
  const body = new URLSearchParams()
  body.set('grant_type', 'refresh_token')
  body.set('refresh_token', rt)
  body.set('client_id', oidcClientId)
  body.set('scope', oidcScope)

  const { data } = await axios.post(`${apiBaseUrl}/connect/token`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  persistTokenResponse(data)
  return data
}

export function logout() {
  clearTokens()
}
